import React from "react";
import { isNoteActivity, type ActivityNode } from "./activity-types";
import { BaseItem } from "./base-item";
import { NoteThread } from "./note-thread";
import { AgeItem } from "./age-item";
import { useAppData } from "#app/lib/data";

interface ActivityItemProps {
  issueId: string;
  activity: ActivityNode;
  last: boolean;
}

/**
 * Routes activities to the appropriate renderer based on changeset type.
 */
export const ActivityItem = React.memo(function ActivityItem({ issueId, activity, last }: ActivityItemProps) {
  const { currentUser } = useAppData();
  const noteAfter = activity.changeset?.note?.after;
  const isNoteDeleted =
    activity.changeset?.note?.before && !activity.changeset?.note?.after;
  const isAgeWithNote = activity.changeset?.age?.after?.noteId;

  if (isNoteActivity(activity) && noteAfter && !isNoteDeleted) {
    return (
      <NoteThread issueId={issueId} noteId={noteAfter} />
    );
  }
  if (isAgeWithNote) {
    // The visit is one combined activity (age + stage move + assignees). Show
    // the stage change and an "assigned to self" tag as extra chips on the note.
    const stageChanged = Boolean(activity.changeset?.stageId);
    const assignees = activity.changeset?.assignees?.after as
      | Array<{ entityId?: string; isPrimary?: boolean }>
      | undefined;
    const assignedToSelf = Boolean(
      currentUser?.userId &&
        assignees?.some(
          (a) => a.entityId === currentUser.userId && a.isPrimary,
        ),
    );
    return (
      <AgeItem
        issueId={issueId}
        noteId={activity.changeset.age.after.noteId}
        ageChangeset={activity.changeset.age}
        reason={activity.reason}
        stageChange={
          stageChanged
            ? {
                oldStage: activity.oldStage,
                newStage: activity.newStage,
                oldWorkflow: activity.oldWorkflow,
                newWorkflow: activity.newWorkflow,
              }
            : null
        }
        assignedToSelf={assignedToSelf}
      />
    );
  }
  return <BaseItem activity={activity} issueId={issueId} last={last} />;
});
